/**
 * Geolocation State Machine Types
 *
 * This module defines the state model for the geolocation subsystem.
 * Key design principles:
 * - Authority (epistemic trust) is separate from Status (operational mode)
 * - Discriminated union enforces invariants at the type level
 */

/**
 * How trustworthy is the current position?
 * - none: No position known
 * - restored: From sessionStorage, unknown provenance (max 30 min)
 * - paused: Was live, tab hidden, known provenance (max 10 min)
 * - live: Active GPS fix, fully trusted
 */
export type GeoAuthority = "none" | "restored" | "paused" | "live"

/**
 * Browser permission state for geolocation
 */
export type GeoPermission = "unknown" | "granted" | "denied" | "prompt"

/**
 * Error codes from the geolocation API
 */
export type GeoErrorCode =
  | "PERMISSION_DENIED"
  | "POSITION_UNAVAILABLE"
  | "TIMEOUT"

/**
 * Error state with metadata
 */
export type GeoError = {
  code: GeoErrorCode
  message?: string
  at: number // timestamp when error occurred
}

/**
 * Watch policy configuration
 * - pauseOnHidden: Whether to pause watching when tab becomes hidden (default: true)
 *   Set to false for navigation/continuous tracking flows
 */
export type WatchPolicy = {
  pauseOnHidden: boolean
}

/**
 * User position with epistemic metadata.
 *
 * - accuracy: GPS accuracy in meters (important for trust decisions)
 * - timestamp: When this fix was obtained (important for freshness)
 */
export type UserPosition = {
  latitude: number
  longitude: number
  accuracy: number | null // meters, null if not provided
  heading: number | null
  timestamp: number // ms since epoch
}

/**
 * Discriminated union for geolocation state.
 * Invalid states are unrepresentable by construction.
 *
 * Invariants enforced by types:
 * - status="watching" requires watchId: number (not null)
 * - status="watching" requires permission: "granted"
 * - status="restored" requires position: UserPosition (not null)
 * - status="paused" requires position: UserPosition (not null)
 * - status="error" requires error: GeoError (not null)
 */
export type GeoState =
  | {
      status: "idle"
      permission: GeoPermission
      authority: "none"
      position: null
      error: null
      watchId: null
      watchPolicy: null
      pausedAt: null
      startedAt: null
    }
  | {
      status: "restored"
      permission: GeoPermission
      authority: "restored"
      position: UserPosition // required in restored state
      error: null
      watchId: null
      watchPolicy: null
      pausedAt: null
      startedAt: null
    }
  | {
      status: "paused"
      permission: GeoPermission
      authority: "paused"
      position: UserPosition // required in paused state
      error: null
      watchId: null
      watchPolicy: WatchPolicy // preserve policy for resume
      pausedAt: number // timestamp for degradation timer
      startedAt: null
    }
  | {
      status: "acquiring"
      permission: GeoPermission // may still be "prompt" until first fix
      authority: "none" | "restored" | "paused" // may have prior position
      position: UserPosition | null
      error: null
      watchId: number // required in acquiring state
      watchPolicy: WatchPolicy
      pausedAt: null
      startedAt: number // for timeout detection
    }
  | {
      status: "watching"
      permission: "granted" // confirmed after first fix
      authority: "live"
      position: UserPosition // required in watching state
      error: null
      watchId: number // required in watching state
      watchPolicy: WatchPolicy
      pausedAt: null
      startedAt: null // cleared after first fix
    }
  | {
      status: "error"
      permission: GeoPermission
      authority: GeoAuthority
      position: UserPosition | null
      error: GeoError // required in error state
      watchId: null
      watchPolicy: WatchPolicy | null
      pausedAt: null
      startedAt: null
    }

/**
 * Initial state for the geolocation slice
 */
export const geoInitialState: GeoState = {
  status: "idle",
  permission: "unknown",
  authority: "none",
  position: null,
  error: null,
  watchId: null,
  watchPolicy: null,
  pausedAt: null,
  startedAt: null,
}
